import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { useEffect, useCallback, useState, useRef, useMemo } from "react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Highlighter,
  Undo,
  Redo,
  ImageIcon,
  Loader2,
} from "lucide-react";
import { adminApi } from "@/features/admin/api/adminApi";
import toast from "react-hot-toast";

interface MiniRichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Inline mode: no borders, toolbar on focus only */
  inline?: boolean;
  /** Callback when editor loses focus */
  onBlur?: () => void;
  /** Section ID for image uploads (required for image support) */
  sectionId?: string;
}

/**
 * A compact rich text editor for section content.
 * Supports inline mode for WYSIWYG editing - no borders, toolbar on focus only.
 * When sectionId is provided, enables image upload support.
 */
export function MiniRichTextEditor({
  content,
  onChange,
  placeholder = "Escribe el contenido de la seccion...",
  autoFocus = false,
  inline = false,
  onBlur,
  sectionId,
}: MiniRichTextEditorProps) {
  const [isFocused, setIsFocused] = useState(autoFocus);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Memoize extensions to prevent recreation on each render
  // This prevents the "Duplicate extension names" warning in TipTap
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false, // No headings in sections - use section title
      }),
      Underline,
      Highlight.configure({
        multicolor: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary-600 underline hover:text-primary-700",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto rounded-lg my-4",
        },
        allowBase64: false,
      }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: content || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => {
      setIsFocused(false);
      onBlur?.();
    },
    editorProps: {
      attributes: {
        class: inline
          ? "prose prose-sm max-w-none focus:outline-none min-h-[60px]"
          : "prose prose-sm max-w-none focus:outline-none min-h-[120px] p-3 border rounded-lg bg-white",
      },
      // Handle image paste and drop
      handleDrop: (view, event, _slice, moved) => {
        if (!sectionId || moved) return false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
          const file = files[0];
          if (file && file.type.startsWith("image/")) {
            event.preventDefault();
            handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: (_view, event) => {
        if (!sectionId) return false;

        const files = event.clipboardData?.files;
        if (files && files.length > 0) {
          const file = files[0];
          if (file && file.type.startsWith("image/")) {
            event.preventDefault();
            handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
    },
    autofocus: autoFocus,
  });

  // Update content when prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content, editor]);

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!editor) return;

      if (!sectionId) {
        toast.error("No se puede subir imagen: sección no identificada");
        console.error("handleImageUpload: sectionId is missing");
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Solo se permiten archivos de imagen");
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("La imagen no puede superar los 10MB");
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      try {
        const result = await adminApi.uploadSectionImage(
          sectionId,
          file,
          (progress) => setUploadProgress(progress),
        );

        // Insert image at current cursor position
        editor.chain().focus().setImage({ src: result.url }).run();
        toast.success("Imagen subida correctamente");
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Error al subir la imagen");
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [sectionId, editor],
  );

  const handleImageButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleImageUpload(file);
      }
      // Reset input so same file can be selected again
      event.target.value = "";
    },
    [handleImageUpload],
  );

  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return inline ? null : (
      <div className="h-[120px] border rounded-lg bg-gray-50 animate-pulse" />
    );
  }

  // In inline mode, show toolbar only when focused
  const showToolbar = inline ? isFocused : true;
  const canUploadImages = !!sectionId;

  return (
    <div className={inline ? "relative" : "space-y-2"}>
      {/* Hidden file input for image uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Mini Toolbar - hidden in inline mode when not focused */}
      {showToolbar && (
        <div
          className={`flex items-center gap-1 flex-wrap p-1 ${
            inline
              ? "bg-white border shadow-lg rounded-lg absolute -top-12 left-0 right-0 z-50"
              : "border rounded-lg bg-gray-50"
          }`}
          // Prevent toolbar clicks from causing editor blur
          onMouseDown={(e) => e.preventDefault()}
        >
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive("bold") ? "bg-gray-200 text-primary-600" : ""
            }`}
            title="Negrita"
          >
            <Bold className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive("italic") ? "bg-gray-200 text-primary-600" : ""
            }`}
            title="Italica"
          >
            <Italic className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive("underline") ? "bg-gray-200 text-primary-600" : ""
            }`}
            title="Subrayado"
          >
            <UnderlineIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive("highlight") ? "bg-gray-200 text-primary-600" : ""
            }`}
            title="Resaltar"
          >
            <Highlighter className="h-4 w-4" />
          </button>

          <div className="w-px h-5 bg-gray-300 mx-1" />

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive("bulletList")
                ? "bg-gray-200 text-primary-600"
                : ""
            }`}
            title="Lista"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive("orderedList")
                ? "bg-gray-200 text-primary-600"
                : ""
            }`}
            title="Lista numerada"
          >
            <ListOrdered className="h-4 w-4" />
          </button>

          <div className="w-px h-5 bg-gray-300 mx-1" />

          <button
            type="button"
            onClick={setLink}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive("link") ? "bg-gray-200 text-primary-600" : ""
            }`}
            title="Enlace"
          >
            <LinkIcon className="h-4 w-4" />
          </button>

          {/* Image button - only shown when sectionId is provided */}
          {canUploadImages && (
            <button
              type="button"
              onClick={handleImageButtonClick}
              disabled={isUploading}
              className={`p-1.5 rounded hover:bg-gray-200 ${
                isUploading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              title="Insertar imagen"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImageIcon className="h-4 w-4" />
              )}
            </button>
          )}

          <div className="flex-1" />

          {/* Upload progress indicator */}
          {isUploading && (
            <span className="text-xs text-gray-500 mr-2">
              {uploadProgress}%
            </span>
          )}

          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-50"
            title="Deshacer"
          >
            <Undo className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-50"
            title="Rehacer"
          >
            <Redo className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Editor Content */}
      <div className={inline ? "relative" : ""}>
        <EditorContent editor={editor} />
      </div>

      {/* Drop zone overlay when dragging */}
      {canUploadImages && isUploading && (
        <div className="absolute inset-0 bg-primary-50/80 border-2 border-dashed border-primary-300 rounded-lg flex items-center justify-center z-20">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-500 mb-2" />
            <p className="text-sm text-primary-700">Subiendo imagen...</p>
          </div>
        </div>
      )}
    </div>
  );
}
